import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
  forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgIconsModule } from "@ng-icons/core";
import { EvaTooltipDirective } from "../eva-tooltip/eva-tooltip.directive";
import { isNgIconName } from "../utils/icon";

/**
 * Interface pour les options du toggle switch
 */
export interface ToggleOption {
  /** Label affiché */
  label?: string;
  /** Valeur associée */
  value: any;
  /** Icône (HeroIcons `hero...` ou PrimeIcons `pi pi-...`) */
  icon?: string;
  /** Tooltip affiché au hover (optionnel) */
  tooltip?: string;
  /** Compteur pour badge (optionnel) */
  count?: number;
  /** Désactive cette option (optionnel) */
  disabled?: boolean;
  /** Texte secondaire affiché sous le label (ex: montant, KPI) */
  subLabel?: string;
  /** Couleur du sous-label (ex: `var(--eva-brand-pink)`, `#eb0086`) */
  subLabelColor?: string;
}

/**
 * EVA Toggle Switch - Composant segmented control multi-états
 *
 * Switch permettant de basculer entre 2 à 5 options avec un slider
 * animé (spring) sur l'option active. Style inspiré du segmented control
 * de tpl-module-list.
 *
 * @example
 * // Toggle simple 2 options
 * <eva-toggle-switch
 *   [options]="[
 *     { label: 'Liste', value: 'list', icon: 'heroListBullet' },
 *     { label: 'Grille', value: 'grid', icon: 'heroSquares2x2' }
 *   ]"
 *   [(value)]="viewMode"
 * ></eva-toggle-switch>
 *
 * // Toggle avec badges compteurs
 * <eva-toggle-switch
 *   [options]="[
 *     { label: 'Tous', value: 'all', count: 42 },
 *     { label: 'Actifs', value: 'active', count: 12 },
 *     { label: 'Archives', value: 'archived', count: 30 }
 *   ]"
 *   [(value)]="filter"
 *   [showCount]="true"
 * ></eva-toggle-switch>
 *
 * // Icônes seules
 * <eva-toggle-switch
 *   [options]="[
 *     { label: '', value: 'light', icon: 'pi pi-sun' },
 *     { label: '', value: 'dark', icon: 'pi pi-moon' }
 *   ]"
 *   [(value)]="theme"
 * ></eva-toggle-switch>
 */
@Component({
  selector: "eva-toggle-switch",
  standalone: true,
  imports: [CommonModule, NgIconsModule, EvaTooltipDirective],
  templateUrl: "./eva-toggle-switch.component.html",
  styleUrls: ["./eva-toggle-switch.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EvaToggleSwitchComponent),
      multi: true,
    },
  ],
})
export class EvaToggleSwitchComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Options du toggle (2 à 5) */
  @Input() options: ToggleOption[] = [];

  /** Taille du toggle */
  @Input() size: "small" | "medium" | "large" = "medium";

  /** Désactive le toggle complet */
  @Input() disabled: boolean = false;

  /** Affiche les badges compteurs */
  @Input() showCount: boolean = false;

  /** Le toggle occupe toute la largeur disponible, options réparties à parts égales */
  @Input() fluid: boolean = false;

  @HostBinding("class.eva-toggle-switch--fluid") get isFluid(): boolean {
    return this.fluid;
  }

  /** ID unique (auto-généré si non fourni) */
  @Input() inputId: string = `eva-toggle-switch-${Math.random().toString(36).substr(2, 9)}`;

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Émis quand la valeur change — pour le two-way binding [(value)] */
  @Output() valueChange = new EventEmitter<any>();

  // ==========================================================================
  // ViewChildren
  // ==========================================================================

  @ViewChildren("optionButton") optionButtons!: QueryList<ElementRef<HTMLButtonElement>>;

  // ==========================================================================
  // Internal state
  // ==========================================================================

  _value: any = null;
  isFocused: boolean = false;
  focusedIndex: number = -1;
  sliderLeft: number = 0;
  sliderWidth: number = 0;

  private resizeObserver: ResizeObserver | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {}

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  ngAfterViewInit(): void {
    requestAnimationFrame(() => this.updateSlider());

    // La pastille de sélection est positionnée en pixels depuis `offsetLeft`/`offsetWidth`.
    // Rendu dans un conteneur encore masqué -- un onglet inactif, une modale qui s'ouvre --,
    // le composant mesure zéro et n'affiche aucune sélection ; rien ne recalculait ensuite,
    // l'onglet devenant visible sans que la valeur ne change. On suit donc la taille réelle.
    //
    // `zone.run` est indispensable : zone.js ne patche pas `ResizeObserver`, donc le callback
    // s'exécute hors zone Angular. `markForCheck()` y salit bien la vue mais ne planifie aucune
    // passe de détection : la pastille restait à 0 px jusqu'au prochain clic de l'utilisateur,
    // n'importe où dans la page.
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.zone.run(() => this.updateSlider()));
      this.resizeObserver.observe(this.host.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  // ==========================================================================
  // Two-way binding [(value)]
  // ==========================================================================

  @Input()
  get value(): any {
    return this._value;
  }
  set value(val: any) {
    if (val !== this._value) {
      this._value = val;
      // Ne PAS émettre valueChange ici — le setter est appelé par les bindings parents,
      // émettre provoquerait une boucle de feedback (ex: topbar ↔ subtopbar).
      // Seul selectOption() (action utilisateur) doit émettre.
      requestAnimationFrame(() => this.updateSlider());
    }
  }

  // ==========================================================================
  // ControlValueAccessor
  // ==========================================================================

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: any): void {
    this._value = value;
    requestAnimationFrame(() => this.updateSlider());
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Computed
  // ==========================================================================

  get selectedIndex(): number {
    return this.options.findIndex((opt) => this.isEqual(opt.value, this._value));
  }

  get isIconOnly(): boolean {
    return this.options.every((opt) => opt.icon && !opt.label);
  }

  get optionsCount(): number {
    return this.options.length;
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  selectOption(option: ToggleOption, index: number): void {
    if (this.disabled || option.disabled) return;

    this._value = option.value;
    this.onChange(this._value);
    this.valueChange.emit(this._value);
    this.onTouched();
    this.updateSlider();
    this.cdr.markForCheck();
  }

  isSelected(option: ToggleOption): boolean {
    return this.isEqual(option.value, this._value);
  }

  isOptionDisabled(option: ToggleOption): boolean {
    return this.disabled || !!option.disabled;
  }

  isHeroIcon(icon: string | undefined): boolean {
    return isNgIconName(icon);
  }

  private isEqual(val1: any, val2: any): boolean {
    // Handle object comparison
    if (typeof val1 === "object" && typeof val2 === "object") {
      return JSON.stringify(val1) === JSON.stringify(val2);
    }
    return val1 === val2;
  }

  // ==========================================================================
  // Focus handling
  // ==========================================================================

  handleFocus(index: number): void {
    this.isFocused = true;
    this.focusedIndex = index;
  }

  handleBlur(): void {
    this.isFocused = false;
    this.focusedIndex = -1;
    this.onTouched();
  }

  // ==========================================================================
  // Keyboard navigation
  // ==========================================================================

  @HostListener("window:resize")
  onResize(): void {
    this.updateSlider();
  }

  @HostListener("keydown", ["$event"])
  handleKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;

    const enabledOptions = this.options.filter((opt) => !opt.disabled);
    if (enabledOptions.length === 0) return;

    const currentIndex = this.selectedIndex;
    let newIndex = currentIndex;

    switch (event.key) {
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        // Find previous enabled option
        newIndex = this.findPreviousEnabledIndex(currentIndex);
        break;

      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        // Find next enabled option
        newIndex = this.findNextEnabledIndex(currentIndex);
        break;

      case "Home":
        event.preventDefault();
        // First enabled option
        newIndex = this.options.findIndex((opt) => !opt.disabled);
        break;

      case "End":
        event.preventDefault();
        // Last enabled option
        for (let i = this.options.length - 1; i >= 0; i--) {
          if (!this.options[i].disabled) {
            newIndex = i;
            break;
          }
        }
        break;

      case "Enter":
      case " ":
        event.preventDefault();
        // Select focused option
        if (this.focusedIndex >= 0 && !this.options[this.focusedIndex]?.disabled) {
          this.selectOption(this.options[this.focusedIndex], this.focusedIndex);
        }
        return;

      default:
        return;
    }

    if (newIndex !== currentIndex && newIndex >= 0) {
      this.selectOption(this.options[newIndex], newIndex);
      // Focus the new option button
      const buttons = this.optionButtons?.toArray();
      if (buttons && buttons[newIndex]) {
        buttons[newIndex].nativeElement.focus();
      }
    }
  }

  private findPreviousEnabledIndex(currentIndex: number): number {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (!this.options[i].disabled) {
        return i;
      }
    }
    // Wrap to end
    for (let i = this.options.length - 1; i > currentIndex; i--) {
      if (!this.options[i].disabled) {
        return i;
      }
    }
    return currentIndex;
  }

  private findNextEnabledIndex(currentIndex: number): number {
    for (let i = currentIndex + 1; i < this.options.length; i++) {
      if (!this.options[i].disabled) {
        return i;
      }
    }
    // Wrap to start
    for (let i = 0; i < currentIndex; i++) {
      if (!this.options[i].disabled) {
        return i;
      }
    }
    return currentIndex;
  }

  // ==========================================================================
  // Slider position (JS-based for variable-width options)
  // ==========================================================================

  updateSlider(): void {
    const buttons = this.optionButtons?.toArray();
    const idx = this.selectedIndex;
    if (!buttons || idx < 0 || !buttons[idx]) {
      this.sliderWidth = 0;
      return;
    }
    const btn = buttons[idx].nativeElement;
    this.sliderLeft = btn.offsetLeft;
    this.sliderWidth = btn.offsetWidth;
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // TrackBy
  // ==========================================================================

  trackByValue(index: number, option: ToggleOption): any {
    return option.value;
  }
}
